import { useUser } from "../hooks";
import UserAccess from "../UserAccess";
import PriorityLabel from "./PriorityLabel";
import { useNavigate } from "react-router-dom";
import { IssuesStatus, UserTag, type Issue } from "../SiteClasses";
import { Table, Icon, Label, Input, Button, IconName, Pagination } from "react-frontier-v2";
import { sortedToPriority } from "../SiteUtil";

interface Props {
   page: number;
   total: number;
   limit: number;
   search: string;
   issues: Issue[];
   tags: UserTag[];
   selectedTags: number[];
   selectedStatuses: number[];
   onPageChange: (page: number) => void;
   onTagsChange: (tags: number[]) => void;
   onSearchChange: (search: string) => void;
   onStatusChange: (statuses: number[]) => void;
}

export var ListTableIssue = (props: Props)=>{
   var { issues, search, onSearchChange, total, page, limit, onPageChange, selectedStatuses, onStatusChange, selectedTags, onTagsChange, tags } = props;
   var navigate = useNavigate();
   var { hasAccess } = useUser();
   return <Table 
      striped 
      selectable
      style={{ width: '1000px', margin: 'auto' }}
   >
      <Table.Head
         title="Tareas"
         titleRight={(
            <div style={{ display: 'flex', gap: 10 }}>
               <Button 
                  color='black' 
                  text='Opciones' 
                  iconRight='caret-down' 
                  iconRightStyle={{ marginRight: 0, marginLeft: 5 }} 
                  size='small'
                  menu={[
                     ...(hasAccess(UserAccess.ISSUES.CREATE_ISSUES)&&[
                        {
                           text: 'Crear tarea',
                           iconName: 'plus-circle' as IconName,
                           onClick: ()=>navigate('/issues/create')
                        }
                     ]),
                     ...(hasAccess(UserAccess.ISSUES.VIEW_ISSUE_TYPES) && [
                        {
                           text: 'Editar tipos',
                           iconName: 'list' as IconName,
                           onClick: ()=>navigate('/issues/types')
                        }
                     ])
                  ]}
               />
            </div>
         )}
      >
         <Table.Row>
            <Table.Cell colSpan={7}>
               <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'baseline', padding: 10 }}>
                  <Input 
                     value={search}
                     onChange={onSearchChange}
                     placeholder='Buscar por ID o título...' 
                  />
                  <Button
                     text='Filtros'
                     iconName='filter'
                     menu={[
                        { 
                           text: 'Limpiar filtros', 
                           iconName: 'trash' as IconName,
                           onClick: ()=>{
                              onSearchChange('');
                              onStatusChange([]);
                              onTagsChange([]);
                           }
                        },
                        { text: 'Estatus', disabled: true, style: { fontWeight: 'bold', opacity: 0.6 } },
                        ...([
                           [IssuesStatus.PENDING, 'Abierto'],
                           [IssuesStatus.IN_PROGRESS, 'En progreso'],
                           [IssuesStatus.ON_HOLD, 'En espera'],
                           [IssuesStatus.RESOLVED, 'Resuelto'],
                           [IssuesStatus.CANCELLED, 'Cancelado'],
                        ] as [IssuesStatus, string][]).map(([s, label])=>({
                           text: label as string,
                           iconName: (selectedStatuses.includes(s) ? 'check-square' : 'square') as IconName,
                           onClick: ()=>{
                              var sNum = Number(s);
                              onStatusChange(
                                 selectedStatuses.includes(sNum) 
                                    ? selectedStatuses.filter(x=>x !== sNum) 
                                    : [...selectedStatuses, sNum]
                              );
                           }
                        })),
                        { text: 'Departamentos', disabled: true, style: { fontWeight: 'bold', opacity: 0.6 } },
                        ...(tags.map((tag)=>({
                           text: tag.tag_name,
                           iconName: (selectedTags.includes(tag.tag_id) ? 'check-square' : 'square') as IconName,
                           onClick: ()=>{
                              onTagsChange(
                                 selectedTags.includes(tag.tag_id)
                                    ? selectedTags.filter(x=>x !== tag.tag_id)
                                    : [...selectedTags, tag.tag_id]
                              );
                           }
                        })))
                     ]}
                  />
               </div>
            </Table.Cell>
         </Table.Row>
         <Table.Row>
            <Table.Cell  header>ID</Table.Cell>
            <Table.Cell header>Titulo</Table.Cell>
            <Table.Cell header>Tipo</Table.Cell>
            <Table.Cell header>Departamento</Table.Cell>
            <Table.Cell header centered>Usuarios</Table.Cell>
            <Table.Cell header centered>Estatus</Table.Cell>
            <Table.Cell header>Prioridad</Table.Cell>
         </Table.Row>
      </Table.Head>
      <Table.Body>
         {issues?.length > 0 ? (
            issues.map((issue)=>{
               var status: { label: string, color: string | null } = { label: 'Desconocido', color: null };
               switch(issue.status?.status_id) {
                  case IssuesStatus.PENDING: status = { label: 'Abierto', color: null }; break;
                  case IssuesStatus.IN_PROGRESS: status = { label: 'En progreso', color: 'blue' }; break;
                  case IssuesStatus.ON_HOLD: status = { label: 'En espera', color: 'orange' }; break;
                  case IssuesStatus.RESOLVED: status = { label: 'Resuelto', color: 'green' }; break;
                  case IssuesStatus.CANCELLED: status = { label: 'Cancelado', color: 'red' }; break;
               }
               
               return (
                  <Table.Row
                     key={issue.issue_id}
                     collapsingIndexes={[0, 2, 4, 5, 6]}
                     centeredIndexes={[4]}
                     style={{ cursor: 'pointer' }}
                     onClick={()=>navigate('/issues/' + issue.issue_id)}
                     data={[
                        issue.issue_id,
                        issue.title,
                        <div style={{display: 'flex', gap: 5, flexWrap: 'wrap'}}>
                           {issue.issue_types.length===1?(
                              <Label iconName={issue.issue_types[0].type_icon as IconName} value={issue.issue_types[0].type_name} />
                           ):sortedToPriority(issue.issue_types).map((type, idx)=>(
                              <Label key={type.type_id} value={idx===0&&type.type_name} iconName={type.type_icon as IconName} />
                           ))}
                        </div>,
                        <Label value={issue.tag?.tag_name || 'Sin depto'} color="orange" style={{ width: '100%' }}/>,
                        <div style={{ display: 'flex' }}>
                           {issue.users && issue.users.length > 0 ? issue.users.map((user, index)=>(
                              <div 
                              key={user.user_id} 
                              style={{ marginLeft: index === 0 ? 0 : -5, width: 20, height: 20, borderRadius: '50%', background: '#313385', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 10, border: '1px solid #fff' }}
                              >
                                 {(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
                              </div>
                           )): <Icon name="minus" color="gray"/>}
                        </div>,
                        <Label value={status.label} style={{ width: '100%' }} color={status.color as any} />,
                        <PriorityLabel priority={issue.priority} />,
                     ]}
                  />
               );
            })
         ) : (
            <Table.Row>
               <Table.Cell colSpan={7} style={{ textAlign: 'center', padding: 20, color: 'gray' }}>
                  No hay tickets
               </Table.Cell>
            </Table.Row>
         )}
      </Table.Body>
      <Table.Footer actions={(
         <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', alignItems: 'center' }}>
            <Pagination
               page={page}
               onPageChange={onPageChange}
               hasNext={(page + 1) * limit < total}
            />
         </div>
      )} />
   </Table>
};

export default ListTableIssue;
